/**
 * Event conversation (Conference Coordinator v2 §4) — create-with-a-name and
 * drive an event through a Gemini interview, from inside Tackle My Day.
 *
 * Left: the coordinator's shows + a "New show" action. Right: the note thread
 * (Allison ↔ Photon), Photon's proposed facts as one-tap confirm chips, and an
 * input to keep talking. No forms — structure is confirmed through conversation.
 *
 * Talks to the v2 intake routes: POST /api/events (create), POST
 * /api/events/{id}/notes (interview turn), POST /api/events/{id}/confirm.
 */
import React, { useCallback, useEffect, useState } from "react";
import {
  makeStyles, tokens, Text, Button, Input, Textarea, Spinner, Badge,
} from "@fluentui/react-components";
import { Add20Regular, Send20Regular, Checkmark16Regular } from "@fluentui/react-icons";

import useCrmApiClient from "../../hooks/useCrmApiClient";

interface Note { role?: string; author?: string; ts?: string; body?: string; }
interface EventDoc {
  id: string; name: string; status?: string;
  notes?: Note[];
  milestones?: Record<string, string | null>;
}
interface ProposedFact {
  field: string; value: string; display?: string; confidence?: string; confirm_question?: string;
}
interface OpenQuestion { question: string; priority?: string; why?: string; }
interface IntakeResult {
  reflect_back?: string; proposed_facts?: ProposedFact[]; open_questions?: OpenQuestion[];
  ready_summary?: string; available?: boolean; reason?: string;
}

const useStyles = makeStyles({
  root: { display: "flex", height: "560px", maxHeight: "70vh", gap: tokens.spacingHorizontalM },
  left: {
    width: "220px", flexShrink: 0, borderRight: `1px solid ${tokens.colorNeutralStroke2}`,
    paddingRight: tokens.spacingHorizontalM, display: "flex", flexDirection: "column", gap: tokens.spacingVerticalXS,
    overflowY: "auto",
  },
  showItem: {
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`, borderRadius: tokens.borderRadiusMedium,
    cursor: "pointer", fontSize: tokens.fontSizeBase300,
  },
  showItemActive: { backgroundColor: tokens.colorNeutralBackground3, fontWeight: tokens.fontWeightSemibold },
  right: { flex: 1, display: "flex", flexDirection: "column", minWidth: 0 },
  thread: { flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: tokens.spacingVerticalS, padding: tokens.spacingVerticalXS },
  msgUser: { alignSelf: "flex-end", maxWidth: "80%", backgroundColor: tokens.colorBrandBackground2, color: tokens.colorNeutralForeground1, padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalM}`, borderRadius: tokens.borderRadiusLarge, whiteSpace: "pre-wrap" },
  msgPhoton: { alignSelf: "flex-start", maxWidth: "85%", backgroundColor: tokens.colorNeutralBackground3, padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalM}`, borderRadius: tokens.borderRadiusLarge, whiteSpace: "pre-wrap" },
  author: { fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3, marginBottom: "2px" },
  chips: { display: "flex", flexWrap: "wrap", gap: tokens.spacingHorizontalXS, padding: `${tokens.spacingVerticalS} 0` },
  chip: { cursor: "pointer" },
  composer: { display: "flex", gap: tokens.spacingHorizontalS, alignItems: "flex-end", borderTop: `1px solid ${tokens.colorNeutralStroke2}`, paddingTop: tokens.spacingVerticalS },
  empty: { flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: tokens.colorNeutralForeground3, textAlign: "center", padding: tokens.spacingHorizontalL },
});

export const EventConversation: React.FC = () => {
  const styles = useStyles();
  const crm = useCrmApiClient();
  const crmRef = React.useRef(crm);
  crmRef.current = crm;

  const [shows, setShows] = useState<EventDoc[]>([]);
  const [selected, setSelected] = useState<EventDoc | null>(null);
  const [intake, setIntake] = useState<IntakeResult | null>(null);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [creatingName, setCreatingName] = useState("");
  const [err, setErr] = useState<string | null>(null);

  const loadShows = useCallback(async (selid?: string) => {
    try {
      const r = await crmRef.current.get<{ events: EventDoc[] }>("/api/events");
      setShows(r.events || []);
      if (selid) {
        const found = (r.events || []).find((e) => e.id === selid);
        if (found) setSelected(found);
      }
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }, []);

  useEffect(() => { void loadShows(); }, [loadShows]);

  const openShow = useCallback(async (id: string) => {
    setIntake(null); setErr(null);
    try {
      const ev = await crmRef.current.get<EventDoc>(`/api/events/${encodeURIComponent(id)}`);
      setSelected(ev);
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
  }, []);

  const createShow = useCallback(async () => {
    const name = creatingName.trim();
    if (!name) return;
    setBusy(true); setErr(null);
    try {
      const r = await crmRef.current.post<{ event: EventDoc }>("/api/events", { name });
      setCreatingName("");
      setSelected(r.event);
      setIntake(null);
      await loadShows(r.event.id);
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  }, [creatingName, loadShows]);

  const send = useCallback(async () => {
    const body = input.trim();
    if (!body || !selected) return;
    setBusy(true); setErr(null); setInput("");
    try {
      const r = await crmRef.current.post<{ intake: IntakeResult; event: EventDoc }>(
        `/api/events/${encodeURIComponent(selected.id)}/notes`, { body },
      );
      setSelected(r.event);
      setIntake(r.intake);
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); setInput(body); }
    finally { setBusy(false); }
  }, [input, selected]);

  const confirmFact = useCallback(async (f: ProposedFact) => {
    if (!selected) return;
    setBusy(true); setErr(null);
    try {
      const ev = await crmRef.current.post<EventDoc>(
        `/api/events/${encodeURIComponent(selected.id)}/confirm`, { field: f.field, value: f.value },
      );
      setSelected(ev);
      // drop the just-confirmed chip
      setIntake((prev) => prev ? { ...prev, proposed_facts: (prev.proposed_facts || []).filter((x) => !(x.field === f.field && x.value === f.value)) } : prev);
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  }, [selected]);

  return (
    <div className={styles.root}>
      <div className={styles.left}>
        <Text weight="semibold" size={200} style={{ color: tokens.colorNeutralForeground3, textTransform: "uppercase" }}>Shows</Text>
        {shows.map((s) => (
          <div
            key={s.id}
            className={`${styles.showItem} ${selected?.id === s.id ? styles.showItemActive : ""}`}
            onClick={() => void openShow(s.id)}
          >
            {s.name}
          </div>
        ))}
        <div style={{ marginTop: tokens.spacingVerticalS, display: "flex", flexDirection: "column", gap: 4 }}>
          <Input
            size="small"
            value={creatingName}
            placeholder="New show name…"
            onChange={(_, d) => setCreatingName(d.value)}
            onKeyDown={(e) => { if (e.key === "Enter") void createShow(); }}
          />
          <Button size="small" icon={<Add20Regular />} disabled={busy || !creatingName.trim()} onClick={() => void createShow()}>
            New show
          </Button>
        </div>
      </div>

      <div className={styles.right}>
        {!selected ? (
          <div className={styles.empty}>
            <Text>Pick a show, or start a new one — then just tell Photon everything you know.</Text>
          </div>
        ) : (
          <>
            <Text weight="semibold" style={{ marginBottom: tokens.spacingVerticalXS }}>
              {selected.name}{selected.status ? ` · ${selected.status}` : ""}
            </Text>
            <div className={styles.thread}>
              {(selected.notes || []).map((n, i) => {
                const isPhoton = (n.role || "").toLowerCase() === "photon" || (n.author || "").toLowerCase() === "photon";
                return (
                  <div key={i} className={isPhoton ? styles.msgPhoton : styles.msgUser}>
                    <div className={styles.author}>{n.author || (isPhoton ? "Photon" : "You")}</div>
                    {n.body}
                  </div>
                );
              })}
              {intake && intake.available === false && intake.reason && (
                <div className={styles.msgPhoton}><Text style={{ color: tokens.colorPaletteRedForeground1 }}>{intake.reason}</Text></div>
              )}
            </div>

            {intake?.proposed_facts && intake.proposed_facts.length > 0 && (
              <div className={styles.chips}>
                <Text size={200} style={{ color: tokens.colorNeutralForeground3, width: "100%" }}>Tap to confirm:</Text>
                {intake.proposed_facts.map((f, i) => (
                  <Badge
                    key={i}
                    className={styles.chip}
                    appearance="outline"
                    color="brand"
                    icon={<Checkmark16Regular />}
                    onClick={() => void confirmFact(f)}
                  >
                    {f.display || `${f.field}: ${f.value}`}
                  </Badge>
                ))}
              </div>
            )}

            {err && <Text size={200} style={{ color: tokens.colorPaletteRedForeground1 }}>{err}</Text>}

            <div className={styles.composer}>
              <Textarea
                style={{ flex: 1 }}
                value={input}
                placeholder="Tell Photon what you know (dates, team, booth, notes)…"
                onChange={(_, d) => setInput(d.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) void send(); }}
                resize="vertical"
              />
              <Button appearance="primary" icon={busy ? <Spinner size="tiny" /> : <Send20Regular />} disabled={busy || !input.trim()} onClick={() => void send()}>
                Send
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default EventConversation;
